import {Button, InputGroup, MenuItem} from '@blueprintjs/core';
import {ItemPredicate, ItemRenderer, Select} from '@blueprintjs/select';
import React, {memo, useCallback} from 'react';
import {stylesheet} from 'typestyle';
import {
  Schema_Validator,
  Schema_Validator_Enum_Element,
} from './proto/deeplay/category_store/v2/category_store';
import {emptyArray} from './utils/empty';

export type ValueInputProps = {
  validator?: Schema_Validator;
  value: string;
  onChange(value: string): void;
  disabled?: boolean;
};

const styles = stylesheet({
  textInputGroup: {
    $nest: {
      '> input': {
        borderRadius: 'inherit !important',
      },
    },
  },
  selectButton: {
    justifyContent: 'space-between',
  },
  selectPopoverTarget: {
    $nest: {
      '> div': {
        borderRadius: 'inherit',
      },
    },
  },
});

export const ValueInput: React.FC<ValueInputProps> = memo(props => {
  const {validator} = props;

  if (validator?.validator?.$case === 'enum') {
    return <EnumInput {...props} />;
  } else {
    return <TextInput {...props} />;
  }
});

const TextInput: React.FC<ValueInputProps> = props => {
  const {value, onChange, disabled} = props;

  return (
    <InputGroup
      fill
      className={styles.textInputGroup}
      disabled={disabled}
      value={value}
      onChange={e => {
        onChange(e.currentTarget.value);
      }}
    />
  );
};

const EnumInput: React.FC<ValueInputProps> = props => {
  const {validator, value, onChange, disabled} = props;

  const enumElements =
    validator?.validator?.$case === 'enum'
      ? validator.validator.enum.elements
      : emptyArray;

  const enumItemRenderer = useCallback<
    ItemRenderer<Schema_Validator_Enum_Element>
  >(
    (item, {handleClick, modifiers}) => {
      if (!modifiers.matchesPredicate) {
        return null;
      }

      return (
        <MenuItem
          key={item.value}
          active={modifiers.active}
          disabled={modifiers.disabled}
          onClick={handleClick}
          text={item.value}
          icon={value === item.value ? 'tick' : 'blank'}
        />
      );
    },
    [value],
  );

  const handleItemSelect = useCallback(
    (item: Schema_Validator_Enum_Element) => {
      onChange(item.value);
    },
    [onChange],
  );

  return (
    <Select<Schema_Validator_Enum_Element>
      disabled={disabled}
      items={enumElements}
      itemRenderer={enumItemRenderer}
      itemPredicate={enumItemPredicate}
      onItemSelect={handleItemSelect}
      popoverProps={{
        targetTagName: 'div',
        targetClassName: styles.selectPopoverTarget,
      }}
    >
      <Button
        className={styles.selectButton}
        fill
        text={value || <> </>}
        rightIcon="double-caret-vertical"
      />
    </Select>
  );
};

const enumItemPredicate: ItemPredicate<Schema_Validator_Enum_Element> = (
  query,
  item,
) => !query || item.value.toLowerCase().startsWith(query.toLowerCase());
